import type { FC, KeyboardEvent } from 'react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { updateDeviationTitle } from '../../../../actions/studio';

import s from './InlineEditableTitle.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
  isEditable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

const InlineEditableTitle: FC<Props> = ({ deviation, isEditable, onFocus, onBlur, className }) => {
  const { t } = useTranslation();
  const id = useId();
  const [value, setValue] = useState(deviation.title ?? '');
  const dispatchUpdateDeviationTitle = useAction(updateDeviationTitle);

  const onSave = useCallback(() => {
    if (value !== deviation.title) {
      dispatchUpdateDeviationTitle(deviation, value);
    }
    onBlur && onBlur();
  }, [dispatchUpdateDeviationTitle, deviation, value, onBlur]);

  const onInputKeyUp = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        onSave();
      } else if (ev.key === 'Escape') {
        setValue(deviation.title ?? '');
        onBlur && onBlur();
      }
    },
    [onSave, onBlur, deviation.title]
  );

  return (
    <div className={cn(s['root'], className)}>
      {isEditable ? (
        <TextInput
          id={id}
          wrapperClassName={s['input-wrapper']}
          className={s['input-field']}
          inputClassname={s['input']}
          label={t('common.title')}
          value={value}
          onChange={e => setValue(e.target.value)}
          onFocus={onFocus}
          onBlur={onSave}
          onKeyUp={onInputKeyUp}
          isLabelHidden
          noBottomSpacing
        />
      ) : (
        <div className={s['title']}>{deviation.title}</div>
      )}
    </div>
  );
};
InlineEditableTitle.displayName = 'InlineEditableTitle';

export default InlineEditableTitle;
